import React, { useState, useMemo } from 'react';
import ExcelJS from 'exceljs';
import Select from 'react-select';
import { styleSelect, themeSelect } from '../../../../../rbts/assets/ts/_utils';
import { formatPrice, LoadingGrid } from '../../../../../rbts/helpers';
import { useObtenerDiasPorEvento } from '../../cortesias/hooks/CortesiasHook';
import { useReportePV } from '../hooks/ReportePVHook';
import { ReportePVRow } from '../../../../service/reportes/ReportePuntoVenta';


interface TipoSummary {
  tipo: string;
  cantidad: number;
  monto_total: number;
  puntosVenta: { nombre: string; cantidad: number; monto_total: number }[];
}

const Vista: React.FC = () => {
  const [diasSeleccionados, setDiasSeleccionados] = useState<any[]>([]);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [expandedTipos, setExpandedTipos] = useState<Set<string>>(new Set());

  const { list: listaDias, loadingRequest: cargandoDias } = useObtenerDiasPorEvento(0);
  const idsDia = diasSeleccionados.map((d: any) => d.value as number);
  const { list, loading } = useReportePV(idsDia);

  const summaryByTipo = useMemo<TipoSummary[]>(() => {
    const map: Record<
      string,
      { tipo: string; cantidad: number; monto_total: number; pvMap: Record<string, { nombre: string; cantidad: number; monto_total: number }> }
    > = {};

    list.forEach((row: ReportePVRow) => {
      if (!map[row.tipo]) {
        map[row.tipo] = { tipo: row.tipo, cantidad: 0, monto_total: 0, pvMap: {} };
      }
      map[row.tipo].cantidad += row.cantidad;
      map[row.tipo].monto_total += row.monto_total;
      if (!map[row.tipo].pvMap[row.nombre]) {
        map[row.tipo].pvMap[row.nombre] = { nombre: row.nombre, cantidad: 0, monto_total: 0 };
      }
      map[row.tipo].pvMap[row.nombre].cantidad += row.cantidad;
      map[row.tipo].pvMap[row.nombre].monto_total += row.monto_total;
    });

    return Object.values(map)
      .map(g => ({
        tipo: g.tipo,
        cantidad: g.cantidad,
        monto_total: g.monto_total,
        puntosVenta: Object.values(g.pvMap).sort((a, b) => a.nombre.localeCompare(b.nombre)),
      }))
      .sort((a, b) => a.tipo.localeCompare(b.tipo));
  }, [list]);

  const totalCantidad = summaryByTipo.reduce((s, g) => s + g.cantidad, 0);
  const totalMonto = summaryByTipo.reduce((s, g) => s + g.monto_total, 0);

  const toggleTipo = (tipo: string) => {
    setExpandedTipos(prev => {
      const next = new Set(prev);
      if (next.has(tipo)) next.delete(tipo);
      else next.add(tipo);
      return next;
    });
  };

  const handleDownloadExcel = async () => {
    setLoadingDownload(true);
    try {
      const workbook = new ExcelJS.Workbook();

      // Sheet 1: Detalle
      const wsDetalle = workbook.addWorksheet('Detalle');
      wsDetalle.columns = [
        { header: 'Fecha', key: 'fecha', width: 14 },
        { header: 'Punto de Venta', key: 'nombre', width: 30 },
        { header: 'Tipo', key: 'tipo', width: 10 },
        { header: 'Cantidad', key: 'cantidad', width: 12 },
        { header: 'Monto Total', key: 'monto_total', width: 16 },
      ];
      wsDetalle.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      wsDetalle.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF8C24' } };
      wsDetalle.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };
      list.forEach((row: ReportePVRow) => {
        wsDetalle.addRow({
          fecha: row.fecha ? new Date(row.fecha).toLocaleDateString('es-MX') : '—',
          nombre: row.nombre,
          tipo: row.tipo,
          cantidad: row.cantidad,
          monto_total: row.monto_total,
        });
      });
      const totalDetRow = wsDetalle.addRow({
        fecha: '',
        nombre: 'TOTAL',
        tipo: '',
        cantidad: totalCantidad,
        monto_total: totalMonto,
      });
      totalDetRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      totalDetRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A1A1A' } };

      // Sheet 2: Resumen
      const wsResumen = workbook.addWorksheet('Resumen');
      wsResumen.columns = [
        { header: 'Tipo / Punto de Venta', key: 'label', width: 34 },
        { header: 'Cantidad', key: 'cantidad', width: 14 },
        { header: 'Monto Total', key: 'monto_total', width: 18 },
      ];
      wsResumen.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      wsResumen.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF8C24' } };
      wsResumen.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };

      summaryByTipo.forEach(grupo => {
        const tipoRow = wsResumen.addRow({
          label: grupo.tipo,
          cantidad: grupo.cantidad,
          monto_total: grupo.monto_total,
        });
        tipoRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        tipoRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A1A1A' } };

        grupo.puntosVenta.forEach(pv => {
          const pvRow = wsResumen.addRow({
            label: `   ${pv.nombre}`,
            cantidad: pv.cantidad,
            monto_total: pv.monto_total,
          });
          pvRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFf0f1f5' } };
        });
      });

      const totalResRow = wsResumen.addRow({
        label: 'Total general',
        cantidad: totalCantidad,
        monto_total: totalMonto,
      });
      totalResRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      totalResRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF8C24' } };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'reporte_puntos_venta.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setLoadingDownload(false);
    }
  };

  return (
    <>
      {/* Filter card */}
      <div className="card mb-6 mb-xl-9">
        <div className="card-header border-0">
          <h3 className="card-title align-items-start flex-column">
            <span className="card-label fw-bold text-gray-800">Búsqueda</span>
          </h3>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-sm-12 col-md-6 col-lg-5 col-xl-4">
              <div className="fs-7 text-gray-700">
                <div className="w-100 d-flex">
                  <label className="form-label w-auto">Días administrativos:</label>
                </div>
                <Select
                  options={listaDias}
                  value={diasSeleccionados}
                  styles={styleSelect}
                  theme={themeSelect}
                  classNamePrefix="tc-select"
                  placeholder="Seleccione uno o varios días..."
                  className="w-100"
                  isSearchable
                  isMulti
                  closeMenuOnSelect={false}
                  isClearable
                  isLoading={cargandoDias}
                  noOptionsMessage={() => <span>No se encontraron registros</span>}
                  onChange={(opts: any) => setDiasSeleccionados(opts ?? [])}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card card-xxl-stretch mb-5 mb-xl-12">
          <div className="card-body py-3">
            <LoadingGrid text="Cargando reporte..." />
          </div>
        </div>
      ) : idsDia.length === 0 ? (
        <div className="card card-xxl-stretch mb-5 mb-xl-12">
          <div className="card-body text-center py-10 text-muted fs-6">
            Selecciona al menos un día administrativo para ver el reporte.
          </div>
        </div>
      ) : list.length === 0 ? (
        <div className="card card-xxl-stretch mb-5 mb-xl-12">
          <div className="card-body text-center py-10 text-muted fs-6">
            No hay ventas para los días seleccionados.
          </div>
        </div>
      ) : (
        <>
          {/* Summary card */}
          <div className="card card-xxl-stretch mb-5 mb-xl-9">
            <div className="card-header border-0">
              <h3 className="card-title align-items-start flex-column">
                <span className="card-label fw-bold text-gray-800">Resumen por Tipo</span>
              </h3>
              <div className="card-toolbar">
                <button
                  className="btn btn-sm btn-light-success"
                  onClick={handleDownloadExcel}
                  disabled={loadingDownload}
                >
                  {loadingDownload ? (
                    <span className="indicator-progress" style={{ display: 'block' }}>
                      Descargando...
                      <span className="spinner-border spinner-border-sm align-middle ms-2" />
                    </span>
                  ) : (
                    'Descargar Excel'
                  )}
                </button>
              </div>
            </div>
            <div className="card-body py-3">
              <div className="table-responsive">
                <table className="table table-rounded border gy-4 gs-7">
                  <thead>
                    <tr className="fw-bolder fs-7 text-gray-800 border-bottom border-gray-200">
                      <th>Tipo / Punto de Venta</th>
                      <th className="text-end">Cantidad</th>
                      <th className="text-end">Monto Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryByTipo.map(grupo => (
                      <React.Fragment key={grupo.tipo}>
                        <tr
                          className="tc-report-group-header cursor-pointer"
                          onClick={() => toggleTipo(grupo.tipo)}
                        >
                          <td className="fw-bold fs-7 ps-3">
                            {expandedTipos.has(grupo.tipo) ? '▼' : '▶'} {grupo.tipo}
                          </td>
                          <td className="text-end fs-7 fw-bold">
                            {grupo.cantidad.toLocaleString('es-MX')}
                          </td>
                          <td className="text-end fs-7 fw-bold">
                            {formatPrice(grupo.monto_total, 'es-MX', 'MXN', 2, 2)}
                          </td>
                        </tr>
                        {expandedTipos.has(grupo.tipo) &&
                          grupo.puntosVenta.map(pv => (
                            <tr key={pv.nombre} className="tc-report-detail">
                              <td className="fs-7 ps-8">{pv.nombre}</td>
                              <td className="text-end fs-7">
                                {pv.cantidad.toLocaleString('es-MX')}
                              </td>
                              <td className="text-end fs-7">
                                {formatPrice(pv.monto_total, 'es-MX', 'MXN', 2, 2)}
                              </td>
                            </tr>
                          ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="tc-report-total-row">
                      <td className="fs-7 ps-3 fw-bolder">Total general</td>
                      <td className="text-end fs-7 fw-bolder">
                        {totalCantidad.toLocaleString('es-MX')}
                      </td>
                      <td className="text-end fs-7 fw-bolder">
                        {formatPrice(totalMonto, 'es-MX', 'MXN', 2, 2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          {/* Detail card */}
          <div className="card card-xxl-stretch mb-5 mb-xl-12">
            <div className="card-header border-0">
              <h3 className="card-title align-items-start flex-column">
                <span className="card-label fw-bold text-gray-800">Detalle de Ventas</span>
                <span className="text-muted mt-1 fw-semibold fs-7">
                  {list.length} registro{list.length !== 1 ? 's' : ''}
                </span>
              </h3>
            </div>
            <div className="card-body py-3">
              <div className="table-responsive">
                <table className="table table-rounded border gy-4 gs-7">
                  <thead>
                    <tr className="fw-bolder fs-7 text-gray-800 border-bottom border-gray-200">
                      <th>Fecha</th>
                      <th>Punto de Venta</th>
                      <th>Tipo</th>
                      <th className="text-end">Cantidad</th>
                      <th className="text-end">Monto Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((row: ReportePVRow) => (
                      <tr key={`${row.id_dia}-${row.id_punto_venta}`} className="align-middle">
                        <td className="fs-7">
                          {row.fecha ? new Date(row.fecha).toLocaleDateString('es-MX') : '—'}
                        </td>
                        <td className="fs-7 fw-bold" style={{ color: 'var(--tc-primary)' }}>
                          {row.nombre}
                        </td>
                        <td className="fs-7">
                          <span className="badge badge-light-primary">{row.tipo}</span>
                        </td>
                        <td className="text-end fs-7">{row.cantidad.toLocaleString('es-MX')}</td>
                        <td className="text-end fs-7">
                          {formatPrice(row.monto_total, 'es-MX', 'MXN', 2, 2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="tc-report-subtotal">
                      <td colSpan={3} className="fs-7 fw-bolder ps-3 tc-report-subtotal-label">
                        Totales
                      </td>
                      <td className="text-end fs-7">
                        {totalCantidad.toLocaleString('es-MX')}
                      </td>
                      <td className="text-end fs-7 fw-bolder tc-amount-positive">
                        {formatPrice(totalMonto, 'es-MX', 'MXN', 2, 2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export { Vista };
